import I18n from 'i18n-js';
import React from 'react';
import BankDepositConfirmCheckbox from './payout_method_currency_warnings';
import PayoutMethodCountryCodeInput from './payout_method_country_code_input';
import RoutingNumberField from './us_routing_number_field';
import ErrorMessage from './payout_method_error_message';
import { PaymentProvider } from './payment_provider';
import Currency from '@reverbdotcom/commons/dist/currency';
import bind from '@reverbdotcom/commons/dist/bind';
import { Result as RoutingNumberResult, Success as RoutingNumberSuccess } from '../lib/verify_bank_routing_number';

export interface Props {
  countryCodes: string[];
  inputNamePrefix: string;
  onCountryChange(countryCode: string): void;
  shopCurrency: string;
  verifyBankRoutingNumber({ routingNumber: string }): Promise<RoutingNumberResult>;
}

interface State {
  bankName: string;
  routingNumberError: string;
  showValidBankInfo: boolean;
}

function formatUSD() {
  const currencyCode = 'USD';
  return I18n.t(`discovery.currencies.${currencyCode}`, { symbol: Currency.get(currencyCode).symbol });
}

export default class USBankFields extends React.Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      routingNumberError: null,
      showValidBankInfo: false,
      bankName: '',
    };
  }

  @bind
  async onRoutingNumberResult(routingNumber: string) {
    const result = await this.props.verifyBankRoutingNumber({ routingNumber });

    if (isRoutingNumberValid(result)) {
      this.success(result);
    } else {
      this.fail();
    }
  }

  success(response: RoutingNumberSuccess) {
    this.setState({
      routingNumberError: null,
      showValidBankInfo: true,
      bankName: response.name,
    });
  }

  fail() {
    this.setState({
      routingNumberError: I18n.t('discovery.payoutMethod.directCheckoutProfile.bankAccountFields.invalidRouting'),
      showValidBankInfo: false,
      bankName: '',
    });
  }

  displayValidBankInfo() {
    if (!this.state.showValidBankInfo) { return null; }
    return (
      <div>
        <hr />
        <p className="mb-0">
          <b>{this.state.bankName}</b>
        </p>
        <hr className="mb-1" />
      </div>
    );
  }

  displayRoutingNumberError() {
    if (!this.state.routingNumberError) { return null; }

    return (
      <ErrorMessage message={this.state.routingNumberError} />
    );
  }

  inputName(methodName) {
    return `${this.props.inputNamePrefix}[${methodName}]`;
  }

  render() {
    return (
      <div>
        <div className="g-container">
          <div className="g-col-6 g-col-mobile-12">
            <label>
              {I18n.t('discovery.payoutMethod.directCheckoutProfile.bankAccountFields.nameOnAccount')}
            </label>
            <input
              className="string required wide mb-0"
              autoFocus
              type="text"
              name={this.inputName('name_on_account')}
            />
          </div>
          <div className="g-col-6 g-col-mobile-12 mobile-mt-1">
            <label>
              {I18n.t('discovery.payoutMethod.directCheckoutProfile.bankAccountFields.accountType')}
            </label>
            <ul className="inline-list horizontal-padded-list scaling-pt-half">
              <li>
                <label className="inline">
                  <input
                    required
                    type="radio"
                    value="checking"
                    defaultChecked
                    name={this.inputName('bank_account_type')}
                  />
                  <span>
                    {I18n.t('discovery.payoutMethod.directCheckoutProfile.bankAccountFields.checkingRadio')}
                  </span>
                </label>
              </li>
              <li>
                <label className="inline">
                  <input
                    required
                    type="radio"
                    value="savings"
                    name={this.inputName('bank_account_type')}
                  />
                  <span>
                    {I18n.t('discovery.payoutMethod.directCheckoutProfile.bankAccountFields.savingsRadio')}
                  </span>
                </label>
              </li>
            </ul>
          </div>
        </div>

        <div className="mb-1">
          <div className="g-container">
            <PayoutMethodCountryCodeInput
              countryCode="US"
              countryCodes={this.props.countryCodes}
              inputName={this.inputName('country_code')}
              onCountryChange={this.props.onCountryChange}
            />
            <div className="g-col-6">
              <label className="string required">
                {I18n.t('discovery.payoutMethod.directCheckoutProfile.bankAccountFields.currencyInput.label')}
              </label>
              <input
                type="hidden"
                name={this.inputName('currency')}
                value="USD"
              />
              <div>{formatUSD()}</div>
            </div>
          </div>

          <BankDepositConfirmCheckbox
            inputName={this.inputName('bank_deposits_confirmed_at')}
            payoutCurrencyCode="USD"
            shopCountryCode="US"
            shopCurrencyCode={this.props.shopCurrency}
            paymentProvider={PaymentProvider.ReverbPayments}
          />
        </div>

        <div className="g-container">
          <div className="g-col-6 g-col-mobile-12">
            <label className="string required">
              {I18n.t('discovery.payoutMethod.directCheckoutProfile.bankAccountFields.routingNumber')}
            </label>
            <RoutingNumberField
              onBlur={this.onRoutingNumberResult}
              name={this.inputName('routing_number')}
            />
          </div>

          <div className="g-col-6 g-col-mobile-12">
            <label className="string required">
              {I18n.t('discovery.payoutMethod.directCheckoutProfile.bankAccountFields.accountNumber')}
            </label>
            <input
              className="string required wide"
              maxLength={17}
              type="text"
              name={this.inputName('bank_account_number')}
            />
          </div>
        </div>
        {this.displayRoutingNumberError()}
        {this.displayValidBankInfo()}
      </div>
    );
  }
}

function isRoutingNumberValid(result: RoutingNumberResult): result is RoutingNumberSuccess {
  return (result as RoutingNumberSuccess).routing_number !== undefined;
}
